import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
} from 'react-hook-form';
import { HookFormComponentProps } from 'ui-component/HookFormComponents/types';
import { ReactNode, useState } from 'react';
import ContextualCreateButton from 'ui-component/ContextualCreateButton';
import { ALL_APPS } from 'constants/appConstants';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { OrgSupplier } from 'types/suppliers';
import { appPermissionAccessLevels } from 'types/apps';
import { HookQuerySearchSelect } from 'ui-component/HookFormComponents/HookQuerySearchSelect';
import { useGetOrgSuppliersServerSideQuery } from 'store/slices/apiV1/suppliers';
import CreateEditOrgSupplierDialog from 'views/suppliers/CreateEditOrgSupplierDialog';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import CreateOrgSupplierV2Dialog from 'views/suppliers/CreateOrgSupplierV2Dialog';

type HookFormOrgSupplierAutocompleteProps = {
  children?: ReactNode;
  defaultValue?: string | string[];
  disabled?: boolean;
  multiple?: boolean;
  sx?: SxProps<Theme>;
  helperText?: string;
  mt?: number;
  fullWidth?: boolean;
  // size is inherited from HookFormComponentProps, but explicitly included here
  // to prevent TypeScript from inferring it as 'unknown' due to the index signature
  size?: 'small' | 'medium';
  setValue: UseFormSetValue<FieldValues>;
  [props: string]: unknown;
  errors: FieldErrors;
  control: Control;
  defaultSupplierObjectId?: OrgSupplier['objectId'];
} & HookFormComponentProps;

export const HookFormOrgSupplierAutocomplete = ({
  errors,
  control,
  name = '',
  label,
  defaultValue,
  disabled,
  multiple = false,
  sx,
  mt = 2,
  helperText,
  fullWidth = true,
  defaultSupplierObjectId,
  setValue,
  size = 'medium',
  ...rest
}: HookFormOrgSupplierAutocompleteProps) => {
  const tempSimplifiedSupplierCreation = useFeature(
    FeatureFlags.tempSimplifiedSupplierCreation
  ).on;
  const [newOrgSupplierDialogOpen, setNewOrgSupplierDialogOpen] =
    useState(false);
  const [contextuallyCreatedOption, setContextuallyCreatedOption] =
    useState<OrgSupplier | null>(null);

  return (
    <>
      <ContextualCreateButton
        onClick={() => setNewOrgSupplierDialogOpen(true)}
        permissionScope={{
          app: ALL_APPS.SUPPLIERS.id,
          accessLevel: appPermissionAccessLevels.edit,
        }}
        size={size}
      >
        <HookQuerySearchSelect<OrgSupplier>
          name={name}
          label={label}
          useGetQuery={useGetOrgSuppliersServerSideQuery}
          errors={errors}
          control={control}
          schema={['name']}
          getOptionLabel={(option: OrgSupplier) => option.name}
          fullWidth
          size={size}
          mb={0}
          contextuallyCreatedOption={contextuallyCreatedOption || undefined}
          filters={
            defaultSupplierObjectId
              ? [
                  {
                    field: 'objectId',
                    operator: '=',
                    value: defaultSupplierObjectId,
                  },
                ]
              : undefined
          }
          autoSelectFirst={!!defaultSupplierObjectId}
        />
      </ContextualCreateButton>
      {tempSimplifiedSupplierCreation ? (
        <CreateOrgSupplierV2Dialog
          dialogOpen={newOrgSupplierDialogOpen}
          onClose={() => setNewOrgSupplierDialogOpen(false)}
          setSelectedSupplier={(value) => {
            setContextuallyCreatedOption(value);
            setValue && setValue(name, value.id);
          }}
        />
      ) : (
        <CreateEditOrgSupplierDialog
          dialogOpen={newOrgSupplierDialogOpen}
          onClose={() => setNewOrgSupplierDialogOpen(false)}
          setSelectedSupplier={(value) => {
            setContextuallyCreatedOption(value);
            setValue && setValue(name, value.id);
          }}
        />
      )}
    </>
  );
};

export default HookFormOrgSupplierAutocomplete;
